// services/analyticsService.js

import { Cookies } from 'react-cookie';
import { FirestoreService } from '../firebase/firestoreService';

const cookies = new Cookies();

/**
 * Track user actions in the application (backend only)
 * @param {string} action - The action being tracked
 * @param {Object} data - Additional data about the action
 */
export const trackUserAction = async (action, data = {}) => {
  try {
    // Always check for consent, even for backend-only tracking
    const hasConsent = cookies.get('consent') === 'true';
    
    // List of essential actions that are always tracked regardless of consent
    const essentialActions = [
      'cookie_consent_accepted', 
      'cookie_consent_rejected',
      'user_logged_in',
      'user_logged_out',
      'page_view'
    ];
    
    // Skip non-essential tracking if no consent
    if (!hasConsent && !essentialActions.includes(action)) {
      console.log('Tracking skipped for action:', action, '(no consent)');
      return;
    }
    
    // Get user ID if available
    const userData = cookies.get('user_data') || {};
    const userId = userData.uid || 'anonymous';
    
    // Prepare activity data
    const activityData = {
      action,
      userId,
      timestamp: new Date().toISOString(),
      page: window.location.pathname,
      userAgent: navigator.userAgent,
      ...data
    };
    
    // Add user context if available from cookies
    if (userData && userData.email) {
      activityData.userEmail = userData.email;
      activityData.username = userData.username || userData.displayName;
    }
    
    // Silently send to Firestore (backend only)
    await storeActivityInFirestore(activityData);
    
    // Log for development purposes only
    if (process.env.NODE_ENV === 'development') {
      console.log('Activity tracked (not visible to users):', activityData);
    }
  } catch (error) {
    // Silently handle errors in production
    if (process.env.NODE_ENV === 'development') {
      console.error('Error tracking user action:', error);
    }
  }
};

/**
 * Store activity data in Firestore (backend only)
 * @param {Object} activityData - The activity data to store
 */
const storeActivityInFirestore = async (activityData) => {
  try {
    // Generate activity ID with timestamp for easier sorting
    const timestamp = new Date().getTime();
    const randomId = Math.random().toString(36).substring(2, 10);
    const activityId = `${timestamp}_${randomId}`;
    
    // Store in Firestore userActivities collection
    await FirestoreService.db.collection('userActivities').doc(activityId).set({
      ...activityData,
      createdAt: FirestoreService.timestamp.now()
    });
  } catch (error) {
    // Silently handle errors in production
    if (process.env.NODE_ENV === 'development') {
      console.error('Error storing activity in Firestore:', error);
    }
  }
};

/**
 * Get user details from cookies - for internal use only
 * @returns {Object} User details
 */
export const getUserDetails = () => {
  try {
    const userData = cookies.get('user_data') || {};
    return userData;
  } catch (error) {
    return {};
  }
};

// Add a hook to track page views automatically
export const usePageViewTracking = () => {
  // Track page view when component mounts
  try {
    const path = window.location.pathname;
    trackUserAction('page_view', { path });
  } catch (error) {
    // Silently fail
  }
};